import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileEdit,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import { previewCVPDFFromDraft, generateCVPDFFromDraft } from "@/lib/recruitmentApi";
import { normalizeApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export const CvPreviewModal = ({
  isOpen,
  onClose,
  draftId,
  candidateName = "Candidat",
  jobTitle = "",
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [isDownloading, setIsDownloading] = useState(false);

  const activeBlobUrlRef = useRef("");

  const cleanupBlob = useCallback(() => {
    if (activeBlobUrlRef.current) {
      if (typeof window !== "undefined" && typeof window.URL?.revokeObjectURL === "function") {
        window.URL.revokeObjectURL(activeBlobUrlRef.current);
      }
      activeBlobUrlRef.current = "";
    }
  }, []);

  const loadPreview = useCallback(async () => {
    if (!draftId) return;
    setIsLoading(true);
    setError("");
    cleanupBlob();

    try {
      const blob = await previewCVPDFFromDraft(draftId);
      const safeBlob =
        blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(safeBlob);
      activeBlobUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);

      const headerPages = Number(blob?.headers?.["x-total-pages"]);
      if (headerPages && Number.isFinite(headerPages) && headerPages > 0) {
        setTotalPages(headerPages);
      } else {
        setTotalPages(1);
      }
      setCurrentPage(1);
    } catch (err) {
      const normalized = normalizeApiError(err, "Impossible de charger la prévisualisation du CV.");
      setError(normalized.message);
      toast.error(normalized.message);
    } finally {
      setIsLoading(false);
    }
  }, [draftId, cleanupBlob]);

  useEffect(() => {
    if (isOpen && draftId) {
      loadPreview();
    } else {
      cleanupBlob();
      setPreviewUrl("");
      setError("");
      setCurrentPage(1);
    }
    return () => {
      cleanupBlob();
    };
  }, [isOpen, draftId, loadPreview, cleanupBlob]);

  const handleDownloadPdf = async () => {
    if (!draftId) return;
    setIsDownloading(true);
    try {
      const pdfBlob = await generateCVPDFFromDraft(draftId);
      const safeBlob =
        pdfBlob instanceof Blob ? pdfBlob : new Blob([pdfBlob], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(safeBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const safeName = (candidateName || "CV").replace(/[^a-zA-Z0-9_-]/g, "_");
      link.download = `CV_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (typeof window !== "undefined" && typeof window.URL?.revokeObjectURL === "function") {
        window.URL.revokeObjectURL(downloadUrl);
      }
      toast.success("Téléchargement du PDF démarré");
    } catch (err) {
      const normalized = normalizeApiError(err, "Erreur lors du téléchargement du PDF.");
      toast.error(normalized.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenBuilder = () => {
    onClose();
    navigate(`/admin/recrutement/cv-builder/${draftId}`);
  };

  const handlePrevPage = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  const handleZoomIn = () => {
    setZoom((z) => Math.min(175, z + 15));
  };

  const handleZoomOut = () => {
    setZoom((z) => Math.max(60, z - 15));
  };

  const handleResetZoom = () => {
    setZoom(100);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseButton
        className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 bg-slate-900 border border-slate-700 text-slate-100 shadow-2xl overflow-hidden"
        data-testid="cv-preview-modal"
      >
        {/* Header toolbar */}
        <DialogHeader className="p-4 bg-slate-950 border-b border-slate-800 flex flex-row items-center justify-between gap-4 space-y-0 shrink-0">
          <div className="flex flex-col min-w-0">
            <DialogTitle className="text-base sm:text-lg font-bold text-white flex items-center gap-2 truncate">
              <span>{candidateName || "Aperçu du CV"}</span>
              {jobTitle && (
                <span className="text-xs font-normal text-slate-400 border-l border-slate-700 pl-2 hidden sm:inline">
                  {jobTitle}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Aperçu haute fidélité PDF conforme à la charte graphique LENVIA
            </DialogDescription>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenBuilder}
              className="hidden md:flex border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs"
              title="Modifier ce CV dans le CV Builder"
            >
              <FileEdit className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
              Éditer dans le Builder
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={isDownloading || isLoading}
              className="hidden sm:flex border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs"
              title="Télécharger le PDF"
            >
              {isDownloading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
              )}
              Télécharger PDF
            </Button>

            {previewUrl && (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs"
                title="Ouvrir le PDF dans un nouvel onglet"
              >
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Nouvel onglet</span>
                </a>
              </Button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 inline-flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ml-1"
              aria-label="Fermer l'aperçu"
              title="Fermer l'aperçu"
              data-testid="close-cv-preview-modal-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        {/* Sub-toolbar (Pages & Zoom) */}
        <div className="px-4 py-2 bg-slate-900 border-b border-slate-800 flex items-center justify-between text-xs text-slate-300 shrink-0">
          {/* Pagination controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage <= 1 || isLoading}
              className="h-7 w-7 p-0 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30"
              title="Page précédente"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-mono text-slate-300">
              Page {currentPage} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages || isLoading}
              className="h-7 w-7 p-0 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30"
              title="Page suivante"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Zoom controls & Refresh */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 60 || isLoading}
              className="h-7 w-7 p-0 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30"
              title="Zoom arrière"
            >
              <Minus className="w-3.5 h-3.5" />
            </Button>
            <button
              onClick={handleResetZoom}
              className="font-mono text-xs text-slate-400 hover:text-white px-1.5 py-0.5 rounded transition-colors"
              title="Réinitialiser le zoom à 100%"
            >
              {zoom}%
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 175 || isLoading}
              className="h-7 w-7 p-0 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-30"
              title="Zoom avant"
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>

            <div className="h-4 w-px bg-slate-700 mx-1" />

            <Button
              variant="ghost"
              size="sm"
              onClick={loadPreview}
              disabled={isLoading}
              className="h-7 text-xs text-slate-300 hover:text-white hover:bg-slate-800"
              title="Actualiser la prévisualisation"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </Button>
          </div>
        </div>

        {/* Viewer content area */}
        <div className="flex-1 bg-slate-950 overflow-auto p-4 flex items-center justify-center relative">
          {isLoading && (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-slate-400">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
              <p className="text-sm font-medium">Génération de l'aperçu PDF haute fidélité…</p>
              <p className="text-xs text-slate-500">Moteur de rendu ReportLab / LENVIA V2</p>
            </div>
          )}

          {!isLoading && error && (
            <div className="max-w-md p-6 bg-slate-900 border border-red-500/30 rounded-xl text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-white">Échec de la prévisualisation</h4>
                <p className="text-xs text-slate-400 leading-relaxed">{error}</p>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                <Button
                  size="sm"
                  onClick={loadPreview}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Réessayer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenBuilder}
                  className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs"
                >
                  <FileEdit className="w-3.5 h-3.5 mr-1.5 text-blue-400" />
                  Ouvrir dans le Builder
                </Button>
              </div>
            </div>
          )}

          {!isLoading && !error && previewUrl && (
            <div
              className="origin-top transition-transform duration-150 shadow-2xl bg-white rounded overflow-hidden"
              style={{
                width: `${zoom}%`,
                maxWidth: "920px",
                minHeight: "600px",
              }}
              data-testid="modal-pdf-frame-container"
            >
              <iframe
                key={`${previewUrl}-p${currentPage}`}
                src={`${previewUrl}#page=${currentPage}&toolbar=0&navpanes=0&view=FitH`}
                title={`Aperçu du CV de ${candidateName}`}
                className="w-full h-[75vh] border-0 bg-white"
                data-testid="cv-modal-pdf-iframe"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CvPreviewModal;
